import { type V2, smoothstep } from '../../core/math';
import { Rng } from '../../core/rng';
import { scatter } from '../../zones/build';
import { type Coast, type LandProfile, type Pier, landHeight, layPier, pierTip, shoreDistance, withPier } from '../../zones/IslandKit';
import type { ZoneLayout } from '../../zones/types';
import { ZoneBuilder } from '../../zones/ZoneBuilder';

const WATER = -0.6;
const DECK = WATER + 0.45;
const COAST: Coast = { x: 0, z: 0, r: 72, waves: [[2, 0.14, 0.4], [3, 0.1, -1.2], [7, 0.055, 0.5]] };
const PROFILE: LandProfile = { seaFloor: -6.5, shore: 0.3, rise: 3, inland: 26, beach: 4, hills: 3.5, seed: 3101 };
const PIER: Pier = { from: { x: 0, z: 66 }, dir: 0, length: 22, halfWidth: 1.5 };
const HILL = { x: -10, z: -22, h: 8, sigma: 18 };

/** Number of Glimmerseeds hidden on the island. */
export const GLIMMER_SEEDS = 15;

/**
 * GLIMMER ISLE — a lush adventure island. Palm beaches ring grassy hills with
 * a vista on the central summit, ancient ruins in the north, a mossy grove in
 * the west where the island's heart grows beside a giant glimmerbloom, and
 * wolves and boars roaming the meadows. 15 `seed` interactables, 3 chests,
 * one `heart`, one `vista`. The pier (south) docks to the Gleaming Sea
 * (`fromGlimmer`). Spawns: `fromSea` (pier) and `start` (beach).
 */
export function buildGlimmer(): ZoneLayout {
  const rng = new Rng('glimmer');
  const b = new ZoneBuilder(
    {
      id: 'glimmer', name: 'Glimmer Isle', subtitle: 'Where the glimmerseeds sprout', kind: 'island', biome: 'meadow', levelRange: [15, 30], music: 'island',
      width: 220, depth: 220, seed: 3101, ground: ['grass', 'sand', 'moss', 'rock'], groundScale: [4, 5, 5, 7], hills: 0, waterLevel: WATER,
      slopeLayer: 3, patchLayer: 2, patchAmount: 0.14, ambient: 'pollen', mapTint: 0x2e6a3a,
    },
    rng,
  );
  const land = (x: number, z: number): number => {
    const d = shoreDistance(COAST, x, z);
    const hill = d < 0 ? HILL.h * Math.exp(-((x - HILL.x) ** 2 + (z - HILL.z) ** 2) / (2 * HILL.sigma ** 2)) * smoothstep(0, 20, -d) : 0;
    return landHeight(COAST, PROFILE, x, z) + hill;
  };
  b.addHeight((x, z) => withPier(land(x, z), PIER, DECK, x, z));
  b.addSurface((x, z, w) => {
    const h = land(x, z);
    w[1] += (1 - smoothstep(WATER + 0.8, WATER + 2.0, h)) * 4;
  });
  const inland = (m: number) => (p: V2) => shoreDistance(COAST, p.x, p.z) < -m;

  // Arrival.
  layPier(b, PIER, DECK);
  const tip = pierTip(PIER, -1.8);
  b.portal({ id: 'glimmer_dock', to: 'sea', toSpawn: 'fromGlimmer', x: tip.x, z: tip.z, radius: 2.4, label: 'The Gleaming Sea', kind: 'dock' });
  b.spawn('fromSea', 0, 77, Math.PI);
  b.spawn('start', 0, 62, Math.PI);
  b.prop('signpost', 3, 63, { collider: { r: 0.3 } });

  // Sandy trail from the beach up to the summit, branching to the ruins.
  b.road([{ x: 0, z: 67 }, { x: -4, z: 44 }, { x: -8, z: 20 }, { x: -10, z: 0 }, { x: -10, z: -14 }], 3, 1, 0.4);
  b.road([{ x: -10, z: -14 }, { x: -6, z: -40 }, { x: 0, z: -56 }], 2.6, 1, 0.4);

  // Summit vista and a chest beside it.
  b.flat({ x: HILL.x, z: HILL.z }, 6, { layer: 1 });
  b.interact({ id: 'gl_vista_summit', kind: 'vista', x: HILL.x, z: HILL.z + 1, data: { name: 'Glimmer Summit' } });
  b.interact({ id: 'gl_chest_summit', kind: 'chest', x: -4, z: -18, data: { loot: 'island_chest' } });

  // Ancient ruins (north) with a chest in the ring.
  b.flat({ x: 0, z: -64 }, 9, { layer: 3 });
  // Eight-column ring, open to the south where the trail arrives.
  for (const k of [0, 1, 2, 3, 5, 6, 7]) {
    const a = (k / 8) * Math.PI * 2;
    b.prop(k % 3 === 0 ? 'pillar_intact' : 'pillar_ruin', Math.sin(a) * 7, -64 - Math.cos(a) * 7, { collider: { r: 0.6 }, rot: k, tint: 0xe8e0c8 });
  }
  b.prop('altar', 0, -69, { collider: { w: 2.2, d: 1.2 }, tint: 0xe8e0c8 });
  b.interact({ id: 'gl_chest_ruins', kind: 'chest', x: 0, z: -63, data: { loot: 'island_chest' } });
  b.interact({ id: 'gl_lore_ruins', kind: 'lore', x: 3, z: -67, data: { lore: 'glimmer_ruins' } });

  // Mossy grove (west): the island heart beside the great glimmerbloom.
  b.flat({ x: -44, z: -36 }, 8, { layer: 2 });
  b.prop('glimmerseed', -46, -38, { scale: 9, collider: { r: 0.14 } }); // collider scales with the prop
  for (const p of Array.from({ length: 9 }, (_, k) => ({ x: -44 + Math.sin((k / 9) * Math.PI * 2) * 6.5, z: -36 + Math.cos((k / 9) * Math.PI * 2) * 6.5 }))) {
    b.prop('mushroom_cluster', p.x, p.z, { scale: 1.1, rot: p.x, noShadow: true });
  }
  b.interact({ id: 'gl_heart', kind: 'heart', x: -42, z: -34, data: { name: 'Heart of the Glimmerbloom' } });

  // Beach cove chest (east).
  b.interact({ id: 'gl_chest_cove', kind: 'chest', x: 58, z: 14, data: { loot: 'island_chest' } });
  b.prop('rock_a', 61, 11, { scale: 1.4, collider: { r: 1.2 } });
  b.prop('log', 55, 17, { rot: 0.7, collider: { w: 2.7, d: 0.7 } });

  // Wildlife.
  b.pack({ id: 'gl_wolves_west', monsters: [{ def: 'vale_wolf', count: 3 }], x: -38, z: 12, radius: 6, level: 16, respawn: 60 });
  b.pack({ id: 'gl_wolves_ridge', monsters: [{ def: 'vale_wolf', count: 3 }], x: 26, z: -30, radius: 6, level: 17, respawn: 60 });
  b.pack({ id: 'gl_boars_meadow', monsters: [{ def: 'thornback_boar', count: 2 }], x: 40, z: 28, radius: 6, level: 16, respawn: 60 });
  b.pack({ id: 'gl_boars_south', monsters: [{ def: 'thornback_boar', count: 3 }], x: -18, z: 44, radius: 6, level: 17, respawn: 60 });

  // Glimmerseeds: spread over the whole island, clear of features.
  const seeds = scatter(rng, { count: GLIMMER_SEEDS, minX: -95, minZ: -95, maxX: 95, maxZ: 95, minDist: 16, maxTries: 6000, accept: (p) => inland(3)(p) && !b.isReserved(p, 1.5) });
  seeds.forEach((p, i) => b.interact({ id: `gl_seed_${i}`, kind: 'seed', x: p.x, z: p.z }));

  // Scenery.
  const all = { minX: -100, minZ: -100, maxX: 100, maxZ: 100 };
  const beach = (p: V2) => {
    const d = shoreDistance(COAST, p.x, p.z);
    return d < -1.5 && d > -8;
  };
  b.scatterProps([{ model: 'tree_palm', weight: 1, collider: { r: 0.4 }, scale: [0.9, 1.35] }], 60, all, 6, beach, 2);
  b.scatterProps([{ model: 'tree_oak', weight: 3, collider: { r: 0.45 }, scale: [0.9, 1.5] }, { model: 'tree_pine', weight: 1.5, collider: { r: 0.45 }, scale: [0.9, 1.4] }], 110, all, 7, inland(9), 3);
  b.scatterProps([{ model: 'rock_a', weight: 2, collider: { r: 1.0 }, scale: [0.7, 1.5] }, { model: 'rock_b', weight: 2, collider: { r: 0.7 }, scale: [0.6, 1.3] }, { model: 'rock_c', weight: 1, collider: { r: 0.7 }, scale: [0.8, 1.4] }], 40, all, 8, inland(4), 2);
  b.scatterProps([{ model: 'bush', weight: 3, scale: [0.8, 1.3], noShadow: true }, { model: 'bush_flower', weight: 4, scale: [0.7, 1.2], noShadow: true }, { model: 'mushroom_cluster', weight: 1, noShadow: true }, { model: 'stump', weight: 0.5, collider: { r: 0.5 } }, { model: 'log', weight: 0.5, collider: { w: 2.7, d: 0.7 } }], 170, all, 3.5, inland(3), 1);
  b.scatterProps([{ model: 'grass_tuft', weight: 5, scale: [0.8, 1.4], noShadow: true }], 380, all, 2.2, inland(2), 0);
  b.scatterProps([{ model: 'reeds', weight: 1, noShadow: true }], 70, { minX: -105, minZ: -105, maxX: 105, maxZ: 105 }, 4, (p) => {
    const d = shoreDistance(COAST, p.x, p.z);
    return d > -2 && d < 0.5;
  }, 1);

  return b.build();
}
